import {
  Button,
  Container,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DEFAULT_WASH_TYPE,
  VEHICLE_WASH_TYPE_OPTIONS,
  WASH_TYPE_LABELS,
} from "../../constants/washTypes";
import { getWorkers } from "../../State/Worker/Action";
import { getWashCentres } from "../../State/WashCentre/Action";
import {
  createCentreBooking,
  getCentreBookings,
} from "../../State/CentreBooking/Action";
import AppPagination from "../Tools/AppPagination";
import moment from "moment";

export default function CentreBooking() {
  const [centreId, setCentreId] = useState("");
  const [vehicleType, setVehicleType] = useState("CAR");
  const [washType, setWashType] = useState(DEFAULT_WASH_TYPE);
  const [washTime, setWashTime] = useState("");
  const [price, setPrice] = useState("");
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [page, setPage] = useState(0);
  const dispatch = useDispatch();
  const { worker, washCentre, centreBooking } = useSelector((store) => store);

  useEffect(() => {
    // Load wash centres, workers, and centre bookings into Redux state
    dispatch(getCentreBookings(page));
    dispatch(getWashCentres());
    dispatch(getWorkers());
  }, [dispatch, page]);

  useEffect(() => {
    if (!VEHICLE_WASH_TYPE_OPTIONS[vehicleType]?.includes(washType)) {
      const fallback =
        VEHICLE_WASH_TYPE_OPTIONS[vehicleType]?.[0] || DEFAULT_WASH_TYPE;
      setWashType(fallback);
    }
  }, [vehicleType, washType]);

  const handleCreateBooking = async (e) => {
    e.preventDefault();

    const payload = {
      centreId,
      vehicleType,
      washType,
      // Send ISO string so backend LocalDateTime can parse it
      washTime: washTime ? new Date(washTime).toISOString() : null,
      price: Number(price || 0),
      workerIds: selectedWorkerIds,
    };

    dispatch(
      createCentreBooking({
        booking: payload,
        onSuccess: () => {
          setCentreId("");
          setWashTime("");
          setPrice("");
          setSelectedWorkerIds([]);
          // refresh list
          dispatch(getCentreBookings(page));
        },
      })
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Create Centre Booking
            </Typography>
            <form onSubmit={handleCreateBooking}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputLabel>Wash Centre</InputLabel>
                  <Select
                    fullWidth
                    value={centreId}
                    onChange={(e) => setCentreId(e.target.value)}
                    displayEmpty
                    required
                  >
                    <MenuItem value="">
                      <em>Select Centre</em>
                    </MenuItem>
                    {washCentre?.centres?.map((centre) => (
                      <MenuItem
                        key={centre.id}
                        value={centre.id}
                      >
                        {centre.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>Vehicle Type</InputLabel>
                  <Select
                    fullWidth
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                  >
                    <MenuItem value="CAR">CAR</MenuItem>
                    <MenuItem value="BIKE">BIKE</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>Wash Type</InputLabel>
                  <Select
                    fullWidth
                    value={washType}
                    onChange={(e) => setWashType(e.target.value)}
                  >
                    {VEHICLE_WASH_TYPE_OPTIONS[vehicleType]?.map((type) => (
                      <MenuItem key={type} value={type}>
                        {WASH_TYPE_LABELS[type] || type}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Wash Time"
                    type="datetime-local"
                    value={washTime}
                    onChange={(e) => setWashTime(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>Workers</InputLabel>
                  <Select
                    fullWidth
                    multiple
                    value={selectedWorkerIds}
                    onChange={(e) => setSelectedWorkerIds(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const workerItem = worker?.workers?.find(
                            (w) => w.id === value || w.workerId === value
                          );
                          const label =
                            workerItem?.name ||
                            workerItem?.fullName ||
                            workerItem?.workerName ||
                            value;
                          return <Chip key={value} label={label} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {worker?.workers?.map((w) => (
                      <MenuItem
                        key={w.id || w.workerId}
                        value={w.id || w.workerId}
                      >
                        {w.name ||
                          w.fullName ||
                          w.workerName ||
                          w.id ||
                          w.workerId}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={centreBooking.loading}
                  >
                    {centreBooking.loading ? "Saving..." : "Create Booking"}
                  </Button>
                </Grid>
                {centreBooking.error && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="body2">
                      Failed to save centre booking
                    </Typography>
                  </Grid>
                )}
                {centreBooking.lastCreatedCentreBooking && (
                  <Grid item xs={12}>
                    <Typography color="success.main" variant="body2">
                      Centre booking created successfully
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </form>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Centre Bookings
            </Typography>
            {centreBooking.loading &&
            centreBooking.pagedCentreBookings?.bookings?.length === 0 ? (
              <Typography variant="body2">Loading...</Typography>
            ) : !centreBooking.pagedCentreBookings?.bookings ||
              centreBooking.pagedCentreBookings.bookings.length === 0 ? (
              <Typography variant="body2">
                No centre bookings found.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {centreBooking.pagedCentreBookings.bookings.map(
                  (booking, index) => (
                    <Grid item xs={12} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, display: "flex", flexDirection: "column" }}
                      >
                        <Typography variant="subtitle1" fontWeight="600">
                          {booking.centreName || booking.centreId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vehicle: {booking.vehicleType} | Wash:{" "}
                          {WASH_TYPE_LABELS[booking.washType] ||
                            booking.washType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Time:{" "}
                          {booking.washTime
                            ? moment(booking.washTime).format(
                                "DD/MM/YYYY hh:mm A"
                              )
                            : "-"}{" "}
                          | Price: ₹
                          {booking.price}
                        </Typography>
                        {booking.workerNames &&
                          booking.workerNames.length > 0 && (
                            <Box
                              mt={1}
                              display="flex"
                              flexWrap="wrap"
                              gap={0.5}
                            >
                              {booking.workerNames.map((name, idx) => (
                                <Chip key={idx} label={name} size="small" />
                              ))}
                            </Box>
                          )}
                      </Paper>
                    </Grid>
                  )
                )}
              </Grid>
            )}
            <AppPagination
              setPage={setPage}
              totalPages={centreBooking.pagedCentreBookings?.totalPages || 0}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}


